/**
 * The per-tab active-firm store (spec multi-product 007 §2.1, §2.6 step 1,
 * 005 §4 "Platform web"; board unit WP1.24.8).
 *
 * INTERNAL. Read it through `active-firm-headers.ts` (the transports) or
 * `useActiveFirm` (React); write it through `useActiveFirm().switchFirm` or the
 * identity sync (`@/entities/active-firm`).
 *
 * ═══ A VANILLA STORE, NOT A HOOK STORE ═══
 *
 * The readers that matter are the HTTP transports (`scopedFetch`, the agent
 * host's header function), which run per request outside React. They read
 * `getState()` synchronously, the same reason the platform-scope store is a
 * module value (`../scope/platform-scope-store.ts`). React reads it with
 * `useStore`.
 *
 * ═══ ONE FIRM PER SCOPE, NOT ONE PER TAB ═══
 *
 * The server admits a claimed firm BEFORE its scope rule
 * (`requested-membership.ts`: the claim wins whenever the person is a member).
 * A person with a brokerage and an underwriter seat gets the underwriter firm
 * from `/underwriter`'s identity read; one shared slot would then claim that
 * firm on every broker request in the tab and serve the brokerage's surfaces
 * from the underwriter organisation. Keying the firm by the scope the request
 * carries keeps phase 1 exactly today's behaviour (007 §2.6: "one membership ⇒
 * that firm, as today") until a switch says otherwise.
 *
 * ═══ OWNED BY ONE PERSON ═══
 *
 * `userId` records whose firms these are. Adopting an identity for a different
 * person drops every firm first, so a tab never claims the previous person's
 * firm on the next person's requests.
 *
 * ═══ CLAIMED IS NOT CHOSEN ═══
 *
 * `firms` is what each scope's requests claim, and the identity sync fills it
 * with whatever firm the server answered identity with. For a person in two
 * firms that answer is the server's pick, not theirs, so it is never a
 * last-used firm (007 APP-13, PR-11: a person in two firms is never put in one
 * silently). `chosen` holds only firms the person picked in this tab
 * (`switchFirm`: the firm picker, the switcher, routing into a firm); it is
 * what the post-sign-in home asks `homeFor` with. An identity answer naming
 * another firm drops the scope's choice (the server no longer admits it).
 *
 * Every value written or rehydrated passes `classifyOrgHeader`: storage is
 * user-editable, so only a well-formed organisation id is ever sent.
 */
import { createStore, type StoreApi } from "zustand"
import { createJSONStorage, persist, type StateStorage } from "zustand/middleware"

import { classifyOrgHeader } from "@/domain/organisation/requested-org"
import { isPlatformScope, type PlatformScope } from "@/domain/scope/platform-scope"

import { tabSessionStorage } from "./tab-session-storage"

/** The organisation id each scope's requests claim in this tab. */
export type ActiveFirms = Readonly<Partial<Record<PlatformScope, string>>>

export interface ActiveFirmState {
  /** Whose firms these are; null before the first identity lands. */
  readonly userId: string | null
  readonly firms: ActiveFirms
  /** The firm the person CHOSE per scope in this tab: the last-used firm. Never set by identity. */
  readonly chosen: ActiveFirms
}

export interface ActiveFirmActions {
  /**
   * The identity read for `scope` answered: this person, this firm. A new
   * person resets every scope first; a null firm (mid-onboarding) clears the
   * scope's claim.
   */
  readonly adoptIdentity: (scope: PlatformScope, identity: { readonly userId: string; readonly orgId: string | null }) => void
  /** The person chose a firm for `scope` in this tab: claimed and remembered as last used. A malformed id is ignored. */
  readonly switchFirm: (scope: PlatformScope, orgId: string) => void
  /**
   * Claim `orgId` for `scope` WITHOUT recording a choice: the person's one
   * firm of that scope, picked for them (a tab opening `/super-admin` claims
   * the one staff firm, STAFF-G5b). A choice for another firm is dropped, one
   * for this firm is kept. A malformed id is ignored.
   */
  readonly claimFirm: (scope: PlatformScope, orgId: string) => void
  /** Signed out: no person, no claims. */
  readonly clear: () => void
}

export type ActiveFirmStore = ActiveFirmState & ActiveFirmActions

/** The sessionStorage key. Versioned through `persist`'s `version`. */
export const ACTIVE_FIRM_STORAGE_KEY = "cova-active-firm"

const EMPTY: ActiveFirmState = { userId: null, firms: {}, chosen: {} }

/** The canonical (lower-case) id, or null when the value is not an organisation id. */
const normaliseOrgId = (orgId: unknown): string | null => {
  if (typeof orgId !== "string") return null
  const claim = classifyOrgHeader(orgId)
  return claim._tag === "Claimed" ? claim.orgId : null
}

const withFirm = (firms: ActiveFirms, scope: PlatformScope, orgId: string | null): ActiveFirms => {
  const next: Partial<Record<PlatformScope, string>> = { ...firms }
  if (orgId === null) delete next[scope]
  else next[scope] = orgId
  return next
}

/** Known scopes with well-formed ids only; anything else is `{}`. */
const sanitiseFirms = (firms: unknown): ActiveFirms => {
  if (typeof firms !== "object" || firms === null) return {}
  const kept: Partial<Record<PlatformScope, string>> = {}
  for (const [scope, orgId] of Object.entries(firms)) {
    const normalised = normaliseOrgId(orgId)
    if (isPlatformScope(scope) && normalised !== null) kept[scope] = normalised
  }
  return kept
}

/**
 * Rehydrated storage is untrusted. A choice survives only while the scope still
 * claims that firm; storage written before `chosen` existed has no choices.
 */
const sanitise = (persisted: unknown): ActiveFirmState => {
  if (typeof persisted !== "object" || persisted === null) return EMPTY
  const { userId, firms, chosen } = persisted as { userId?: unknown; firms?: unknown; chosen?: unknown }
  if (typeof userId !== "string" || typeof firms !== "object" || firms === null) return EMPTY
  const keptFirms = sanitiseFirms(firms)
  const keptChosen: Partial<Record<PlatformScope, string>> = {}
  for (const [scope, orgId] of Object.entries(sanitiseFirms(chosen))) {
    if (isPlatformScope(scope) && keptFirms[scope] === orgId) keptChosen[scope] = orgId
  }
  return { userId, firms: keptFirms, chosen: keptChosen }
}

/**
 * A store over `storage`. The app uses the singleton below over the tab's
 * `sessionStorage`; a test builds one per simulated tab.
 */
export const createActiveFirmStore = (storage: StateStorage = tabSessionStorage()): StoreApi<ActiveFirmStore> =>
  createStore<ActiveFirmStore>()(
    persist(
      (set, get) => ({
        ...EMPTY,
        adoptIdentity: (scope, identity) => {
          const { userId, firms, chosen } = get()
          const samePerson = userId === identity.userId
          const orgId = normaliseOrgId(identity.orgId)
          const ownChosen = samePerson ? chosen : {}
          // The server admitted the choice ⇒ keep it; it answered another firm (or none) ⇒ the choice is gone.
          const keptChoice = ownChosen[scope] === orgId ? orgId : null
          set({
            userId: identity.userId,
            firms: withFirm(samePerson ? firms : {}, scope, orgId),
            chosen: withFirm(ownChosen, scope, keptChoice),
          })
        },
        switchFirm: (scope, orgId) => {
          const normalised = normaliseOrgId(orgId)
          if (normalised === null) return
          const { firms, chosen } = get()
          set({ firms: withFirm(firms, scope, normalised), chosen: withFirm(chosen, scope, normalised) })
        },
        claimFirm: (scope, orgId) => {
          const normalised = normaliseOrgId(orgId)
          if (normalised === null) return
          const { firms, chosen } = get()
          set({
            firms: withFirm(firms, scope, normalised),
            chosen: withFirm(chosen, scope, chosen[scope] === normalised ? normalised : null),
          })
        },
        clear: () => set(EMPTY),
      }),
      {
        name: ACTIVE_FIRM_STORAGE_KEY,
        version: 1,
        storage: createJSONStorage(() => storage),
        partialize: ({ userId, firms, chosen }) => ({ userId, firms, chosen }),
        merge: (persisted, current) => ({ ...current, ...sanitise(persisted) }),
      },
    ),
  )

/** The tab's store. Server code never writes it, so on the server it stays empty. */
export const activeFirmStore = createActiveFirmStore()
